import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControlLabel } from '@mui/material';

import useSurveySettings from 'src/hooks/useSurveySettings';
import type { FilterValue } from 'src/components/app/context/FilterContext';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import { AccordionItemHeader } from 'src/components/common/Accordion';
import AccordionItem from 'src/components/common/Accordion/components/AccordionItem';
import AccordionItemPanel from 'src/components/common/Accordion/components/AccordionItemPanel';
import { FILTER_KEYS } from 'src/components/followUp/hooks/constants';
import type { DateRangeAsStrings } from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';
import {
  defaultDateRange,
  filterToDateRange,
} from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';

import DateFilterControl from '../DateFilter/DateFilterControl';

import { comparisonDateRangeFilter } from './helper';

export const COMPARISON_FILTER_NAME = 'comparisonFilter';

interface ComparisonFilterProps {
  range?: boolean;
}

const ComparisonFilter: FC<ComparisonFilterProps> = ({ range }) => {
  const { t } = useTranslation('followUp');
  const { filterGroups, setFilter, removeFilter } = useFilterContext();
  const rangeChecked =
    !!filterGroups[FILTER_KEYS.DATE_RANGE_COMPARISON]?.length;
  const dateRangeFilter = filterGroups[FILTER_KEYS.DATE_RANGE]?.[0];
  const { defaultComparisonDateRange } = useSurveySettings();

  const onChangeRangeChecked = useCallback(
    (_: unknown, checked: boolean) => {
      if (checked) {
        const currentDateRange = dateRangeFilter
          ? filterToDateRange(
              dateRangeFilter as FilterValue<DateRangeAsStrings>,
            )
          : defaultDateRange;
        setFilter(FILTER_KEYS.DATE_RANGE_COMPARISON, [
          comparisonDateRangeFilter(
            defaultComparisonDateRange,
            currentDateRange,
          ),
        ]);
      } else {
        removeFilter(
          FILTER_KEYS.DATE_RANGE_COMPARISON,
          FILTER_KEYS.DATE_RANGE_COMPARISON,
        );
      }
    },
    [dateRangeFilter, defaultComparisonDateRange, removeFilter, setFilter],
  );

  return (
    <AccordionItem itemName={COMPARISON_FILTER_NAME}>
      <AccordionItemHeader
        headline={t('comparisonFilter.comparison')}
        showSelectedFilterCountForKey={[FILTER_KEYS.DATE_RANGE_COMPARISON]}
      />
      <AccordionItemPanel>
        {range && (
          <>
            <FormControlLabel
              label={t('comparisonFilter.timeComparison')}
              control={
                <Checkbox
                  checked={rangeChecked}
                  onChange={onChangeRangeChecked}
                />
              }
            />
            {rangeChecked && <DateFilterControl comparison />}
          </>
        )}
      </AccordionItemPanel>
    </AccordionItem>
  );
};

export default ComparisonFilter;
